import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReCAPTCHA from 'react-google-recaptcha';
import { Phone, Mail, Clock, ArrowRight, Shield, ChevronDown, ChevronUp, Building, Hammer, Home, Ruler, Send, Loader2, ChevronLeft, ChevronRight, Instagram, Facebook, Linkedin, Smartphone, Search, Package, Wrench, Users, ShieldCheck, Headset, Building2 } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { PageSEOFooter } from '../components/seo/PageSEO';
import { BreadcrumbsContainer } from '../components/seo/Breadcrumbs';
import { isValidPhone, PHONE_ERROR_MESSAGE } from '../utils/validatePhone';
import TermsAcceptCheckbox from '../components/forms/TermsAcceptCheckbox';
import BrandIcon from '../components/icons/BrandIcon';
import { useIsSA } from '../lib/userState';
import { clayIcon } from '../lib/clayIcons';

// reCAPTCHA site key - if unset, the widget is hidden and the form works
// without CAPTCHA. Honeypot + time-check below still guard against bots.
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';
const RECAPTCHA_ENABLED = !!RECAPTCHA_SITE_KEY;

// Bots submit forms in <500ms; humans need at least a few seconds to
// fill fields and click. Anything below this = silent reject.
const MIN_FORM_FILL_MS = 3000;

// All testimonials from building type pages
const allTestimonials = [
  {
    quote: "BuildPilot assessed our site and highlighted things we never would have picked up ourselves. That advice gave us clarity and confidence before committing to the build.",
    author: "Russell Davies",
    location: "Adelaide, SA",
    initials: "RD"
  },
  {
    quote: "BuildPilot reviewed multiple land options and gave me honest advice without any pressure to build. Their guidance helped me realise building wasn't the right choice for me, which I'm genuinely grateful for.",
    author: "Effie Giannakakis",
    location: "Adelaide, SA",
    initials: "EG"
  },
  {
    quote: "The team at BuildPilot made what seemed like a daunting process smooth and enjoyable. Our dream home is now a reality, thanks to them.",
    author: "Rinaldi Residence",
    location: "Magill, SA",
    initials: "RR",
    image: "/images/testimonials/magill.avif"
  },
  {
    quote: "Building a home can be overwhelming, but George and BuildPilot made the entire process simple and efficient. We couldn't be more grateful!",
    author: "Sterai Residence",
    location: "Ingle Farm, SA",
    initials: "SR",
    image: "/images/testimonials/ingle-farm.avif"
  },
  {
    quote: "We'd been searching for months trying to find the right builder for our extension. BuildPilot not only found us someone perfect, but helped us visualise exactly what the finished result would look like.",
    author: "Boutros Family",
    location: "Adelaide, SA",
    initials: "BF",
    image: "/images/testimonials/boutros.avif"
  },
  {
    quote: "We needed help finding the right builder for our project. BuildPilot assessed our situation and matched us with a fantastic builder who completed our home beautifully.",
    author: "Anyek Residence",
    location: "Adelaide, SA",
    initials: "AR",
    image: "/images/testimonials/forgan.avif"
  },
  {
    quote: "Thank you for being so genuine and a good human George. We didn't even know what a Torrens Title was!",
    author: "Sara Residence",
    location: "Adelaide, SA",
    initials: "SR",
    image: "/images/testimonials/ochieng.avif"
  },
  {
    quote: "Subdividing our corner block in Moana seemed overwhelming with all the retaining walls and service runs. BuildPilot broke it all down and made what felt impossible actually achievable.",
    author: "Russell Davies",
    location: "Moana, SA",
    initials: "RD"
  },
  {
    quote: "BuildPilot designed our verandah with the future in mind. They helped us find a builder who understood that we eventually want to close it in and extend the home.",
    author: "Boutros Family",
    location: "Two Wells, SA",
    initials: "BF"
  },
  {
    quote: "Thank you, George. You've helped more than you could know. You are a marvel and so patient.",
    author: "C. Foster",
    location: "Glenelg North, SA",
    initials: "CF"
  },
  {
    quote: "Chris has been restoring his Holden HQ Monaro for years, so getting the garage right was non-negotiable. BuildPilot helped us design the home with garage access in mind, then found us a quality shed builder.",
    author: "Tanya & Chris",
    location: "Christies Beach, SA",
    initials: "TC"
  },
  {
    quote: "BuildPilot turned our vision into reality. Their expertise and commitment to getting the details right made all the difference.",
    author: "Shields Residence",
    location: "Semaphore, SA",
    initials: "SR"
  },
  {
    quote: "We couldn't have done it without BuildPilot! Their guidance through the design and builder selection process was spot on.",
    author: "Leslie Residence",
    location: "Munno Para West, SA",
    initials: "LR"
  },
  {
    quote: "We struggled to find plans in a display so that's why we wanted to find someone like George to help us.",
    author: "Forgan Residence",
    location: "Salisbury Heights, SA",
    initials: "FR"
  }
];

// FAQ Accordion for SEO
const FaqItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border-b border-gray-200 last:border-b-0">
    <button
      onClick={onClick}
      className="w-full py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors px-1"
    >
      <span className="font-medium text-[#1d1d1f] pr-4">{question}</span>
      {isOpen ? (
        <ChevronUp className="w-5 h-5 text-[#1d8efe] flex-shrink-0" />
      ) : (
        <ChevronDown className="w-5 h-5 text-[#86868b] flex-shrink-0" />
      )}
    </button>
    {isOpen && (
      <div className="pb-5 px-1">
        <p className="text-[#86868b] leading-relaxed">{answer}</p>
      </div>
    )}
  </div>
);

// Service selection card - Full version for desktop
const ServiceCard = ({ icon: Icon, title, description, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`p-4 rounded-xl border-2 text-left transition-all ${
      selected 
        ? 'border-[#1d8efe] bg-[#1d8efe]/5 ring-2 ring-[#1d8efe]/20' 
        : 'border-gray-200 hover:border-[#1d8efe]/50 hover:bg-gray-50'
    }`}
  >
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${
      selected ? 'bg-[#1d8efe] text-white' : 'bg-gray-100 text-[#86868b]'
    }`}>
      <Icon className="w-5 h-5" />
    </div>
    <h3 className={`font-semibold mb-1 ${selected ? 'text-[#1d8efe]' : 'text-[#1d1d1f]'}`}>
      {title}
    </h3>
    <p className="text-sm text-[#86868b]">{description}</p>
  </button>
);

// Compact Service Card for mobile - smaller, no description
const ServiceCardCompact = ({ icon: Icon, title, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`p-3 rounded-xl border-2 text-center transition-all ${
      selected 
        ? 'border-[#1d8efe] bg-[#1d8efe]/5' 
        : 'border-gray-200 hover:border-[#1d8efe]/50 bg-white'
    }`}
  >
    <div className={`w-9 h-9 rounded-lg flex items-center justify-center mx-auto mb-2 ${
      selected ? 'bg-[#1d8efe] text-white' : 'bg-gray-100 text-[#86868b]'
    }`}>
      <Icon className="w-4 h-4" />
    </div>
    <p className={`text-xs font-medium ${selected ? 'text-[#1d8efe]' : 'text-[#1d1d1f]'}`}>
      {title}
    </p>
  </button>
);

// Trust badge component with animated gradient
const TrustBadge = ({ icon: Icon, stat, label }) => (
  <div className="flex items-center gap-3">
    <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
      <Icon className="w-6 h-6 text-white" />
    </div>
    <div>
      <p className="text-2xl font-bold text-white">{stat}</p>
      <p className="text-sm text-white/70">{label}</p>
    </div>
  </div>
);

// Testimonial Card for carousel
const TestimonialCard = ({ testimonial }) => (
  <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 h-full flex flex-col">
    <div className="flex gap-1 mb-4">
      {[...Array(5)].map((_, i) => (
        <BrandIcon name="star" key={i} className="w-4 h-4 fill-[#e8713a] text-[#e8713a]" />
      ))}
    </div>
    <blockquote className="text-[#1d1d1f] flex-1 mb-4 leading-relaxed">
      "{testimonial.quote}"
    </blockquote>
    <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
      {testimonial.image ? (
        <img 
          src={testimonial.image} 
          alt={testimonial.author}
          className="w-12 h-12 rounded-full object-cover" loading="lazy" decoding="async" />
      ) : (
        <div className="w-12 h-12 rounded-full bg-[#1d8efe] flex items-center justify-center text-white font-semibold">
          {testimonial.initials}
        </div>
      )}
      <div>
        <p className="font-semibold text-[#1d1d1f]">{testimonial.author}</p>
        <p className="text-sm text-[#86868b]">{testimonial.location}</p>
      </div>
    </div>
  </div>
);

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    projectStage: '',
    message: '',
    preferredContact: 'phone'
  });
  const [captchaToken, setCaptchaToken] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [formStep, setFormStep] = useState(1);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [errors, setErrors] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const recaptchaRef = useRef(null);

  // Anti-spam: record when the form first mounts. Submissions faster than
  // MIN_FORM_FILL_MS are silently ignored (bots submit in <500ms).
  const formLoadedAtRef = useRef(Date.now());
  // Honeypot: bots fill every visible-looking field. Real users never see
  // this input (aria-hidden + off-screen positioning). If populated on
  // submit we silently no-op.
  const [honeypot, setHoneypot] = useState('');
  const [helpQuery, setHelpQuery] = useState('');
  const isSA = useIsSA();

  const services = [
    { id: 'new-home', icon: Home, title: 'New Home Build', description: 'Building from scratch' },
    { id: 'renovation', icon: Hammer, title: 'Renovation', description: 'Updating existing home' },
    { id: 'extension', icon: Building, title: 'Extension', description: 'Adding to your home' },
    { id: 'knockdown', icon: Ruler, title: 'Knockdown Rebuild', description: 'Start fresh on your block' },
  ];

  const projectStages = [
    { value: 'researching', label: "Just researching" },
    { value: 'planning', label: "Planning stage" },
    { value: 'ready', label: "Ready to start" },
    { value: 'builder-issues', label: "Having builder issues" },
  ];

  const faqs = [
    {
      q: "How much does BuildPilot's service cost?",
      a: "Our pricing depends on the level of support you need. We'll discuss your specific requirements during our initial conversation and provide a clear quote before you commit to anything."
    },
    {
      q: "How quickly will someone contact me?",
      a: "We'll be in touch as soon as we can. For urgent matters, call us directly and we'll prioritise your enquiry."
    },
    {
      q: "What areas do you service?",
      a: "Our one-on-one building support is currently available for South Australian and Victorian residents. Our self-guided BuildPilot app is available nationwide across Australia."
    },
    {
      q: "Do I need plans or quotes already?",
      a: "Not at all! We can help you at any stage of your building journey, whether you're just exploring options or already have quotes to compare."
    },
    {
      q: "How do you select your trusted builders?",
      a: "Every builder in our network goes through a rigorous vetting process including licence verification, insurance checks, financial stability assessment, and review of past project quality."
    },
  ];

  // Auto-advance testimonials
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % allTestimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleServiceSelect = (serviceId) => {
    setFormData(prev => ({ ...prev, service: serviceId }));
  };

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
    if (errors.captcha) {
      setErrors(prev => ({ ...prev, captcha: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate phone format
    if (!isValidPhone(formData.phone)) {
      setErrors(prev => ({ ...prev, phone: PHONE_ERROR_MESSAGE }));
      return;
    }

    // Validate captcha only if reCAPTCHA is enabled for this deployment.
    if (RECAPTCHA_ENABLED && !captchaToken) {
      setErrors(prev => ({ ...prev, captcha: 'Please complete the reCAPTCHA verification' }));
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          service: formData.service,
          message: `Project Stage: ${formData.projectStage}\nPreferred Contact: ${formData.preferredContact}\n\n${formData.message}`,
          captchaToken,
          privacy_consent: true,
          source_page: typeof window !== 'undefined' ? window.location.pathname : null,
          // Anti-spam signals (silently evaluated server-side)
          honeypot,
          time_on_page_ms: Date.now() - formLoadedAtRef.current,
        })
      });

      if (response.ok) {
        setIsSubmitted(true);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % allTestimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + allTestimonials.length) % allTestimonials.length);
  };

  const canProceedStep1 = formData.service !== '';
  const canProceedStep2 = formData.name && formData.email && formData.phone;

  // Schema markup for SEO
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact BuildPilot",
    "description": "Get in touch with BuildPilot. Talk to a real human about your Adelaide build - partner builder shortlists, contract review or One on One Support.",
    "url": "https://buildpilot.com.au/contact",
    "mainEntity": {
      "@type": "Organization",
      "name": "BuildPilot",
      "url": "https://buildpilot.com.au",
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+61-1300-885-919",
        "contactType": "customer service",
        "areaServed": "AU",
        "availableLanguage": "English"
      }
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://buildpilot.com.au" },
      { "@type": "ListItem", "position": 2, "name": "Contact", "item": "https://buildpilot.com.au/contact" }
    ]
  };

  // Related guidance for internal linking
  const relatedGuidance = [
    { title: "Where We Fit In", slug: "/where-we-fit-in", description: "Understand how we guide your building journey" },
    { title: "Why BuildPilot", slug: "/why-buildpilot", description: "What makes us different from builders and brokers" },
    { title: "Our Builders", slug: "/home-builders-adelaide", description: "How we match you with curated builders, and the tools to check anyone yourself" },
    { title: "New Home Builds", slug: "/building-a-new-home", description: "Comprehensive guide to building in Adelaide" }
  ];

  // Help-hub topic tiles. One-on-One Support is South Australia only.
  const helpTopics = [
    { id: 'match-builders', title: 'Get matched with builders', description: 'Find vetted home builders matched to your budget, site and location.', link: '/workspace/tools/find-a-builder', icon: Hammer, img: clayIcon.builders, category: 'Matching', testId: 'help-topic-match-builders' },
    { id: 'match-suppliers', title: 'Find materials & suppliers', description: 'Direct access to kitchen, bath, flooring, stone and energy suppliers.', link: '/suppliers', icon: Package, img: clayIcon.suppliers, category: 'Matching', testId: 'help-topic-suppliers' },
    { id: 'match-trades', title: 'Connect with licensed trades', description: 'Trusted tradies for concreting, roofing, electrical, plumbing and tiling.', link: '/find/category/tradies', icon: Wrench, img: clayIcon.trades, category: 'Matching', testId: 'help-topic-trades' },
    { id: 'match-pros', title: 'Professional services', description: 'Architects, certifiers, conveyancers and building designers.', link: '/find/category/professional-services', icon: Ruler, img: clayIcon.pros, category: 'Matching', testId: 'help-topic-pros' },
    { id: 'partner-business', title: 'Partner with BuildPilot', description: 'List your building, supplier, trade or pro business on our network.', link: '/register-business', icon: Building2, img: clayIcon.partner, category: 'Partnership', badge: 'For business', testId: 'help-topic-partner' },
    { id: 'community-groups', title: 'Community groups', description: 'Connect with thousands of local homeowners to ask questions and compare notes.', link: '/community', icon: Users, img: clayIcon.community, category: 'Community', testId: 'help-topic-community' },
    { id: 'app-download', title: 'BuildPilot mobile app', description: 'Download our iOS and Android app for trackers, checklists and tools.', link: 'https://buildpilot.app', icon: Smartphone, img: clayIcon.app, category: 'App', external: true, testId: 'help-topic-app' },
    { id: 'sa-support', title: 'One-on-One support', description: 'Dedicated South Australian building guidance and site support.', link: '/one-on-one-support', icon: Headset, img: clayIcon.oneOnOne, category: 'Advisory', badge: 'SA only', saOnly: true, testId: 'help-topic-sa-support' },
    { id: 'how-it-works', title: 'How BuildPilot works', description: 'Our independent guidance model, builder verification and trust.', link: '/how-it-works', icon: ShieldCheck, img: clayIcon.howItWorks, category: 'General', testId: 'help-topic-how-it-works' },
  ].filter((t) => (t.saOnly ? isSA : true));
  const helpQ = helpQuery.trim().toLowerCase();
  const filteredTopics = helpQ
    ? helpTopics.filter((t) => `${t.title} ${t.description} ${t.category}`.toLowerCase().includes(helpQ))
    : helpTopics;

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Contact BuildPilot Adelaide (2026): Talk to a Real Human About Your Build</title>
        <meta name="title" content="Contact BuildPilot Adelaide (2026): Talk to a Real Human About Your Build" />
        <meta name="description" content="Talk to a real human about your Adelaide build. Your CoPilot to building in Adelaide - new homes, knockdown rebuilds, renovations and One on One Support. Call 1300 885 919 or book online." />
        <meta name="keywords" content="contact BuildPilot, BuildPilot Adelaide phone, BuildPilot email, Adelaide home build CoPilot, One on One Support Adelaide" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://buildpilot.com.au/contact" />
        
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://buildpilot.com.au/contact" />
        <meta property="og:title" content="Contact BuildPilot Adelaide | Talk to a Real Human" />
        <meta property="og:description" content="Talk to a real human about your Adelaide build. We respond as soon as we can." />
        <meta property="og:site_name" content="BuildPilot" />
        <meta property="og:locale" content="en_AU" />
        
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Contact BuildPilot Adelaide" />
        <meta name="twitter:description" content="Talk to a real human about your Adelaide build. We respond as soon as we can." />
        
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <Header />

      {/* Breadcrumb Navigation */}
      <div className="absolute top-28 sm:top-32 left-0 right-0 z-10">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-6">
          <nav className="flex items-center gap-2 text-sm text-white/60">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <span className="text-white">Contact</span>
          </nav>
        </div>
      </div>

      {/* Hero - Help/Support Hub search */}
      <section className="pt-36 sm:pt-44 lg:pt-44 pb-14 sm:pb-20 gradient-blue-bg relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-[400px] sm:w-[600px] h-[400px] sm:h-[600px] bg-white rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 left-0 w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] bg-white rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
        </div>

        <div className="max-w-[900px] mx-auto px-5 sm:px-6 relative text-center">
          <h1 className="text-[32px] sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-[-0.03em] mb-4" data-testid="contact-hero-heading">
            Need help? You're in the right place
          </h1>
          <p className="text-base sm:text-xl text-white/80 max-w-2xl mx-auto mb-8 leading-relaxed">
            Search our help topics, get matched with builders, suppliers and trades, or send our team a message.
          </p>

          <div className="max-w-2xl mx-auto relative">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717a] pointer-events-none" />
            <input
              type="text"
              value={helpQuery}
              onChange={(e) => setHelpQuery(e.target.value)}
              placeholder="Search help topics (e.g. 'find a builder', 'list my business', 'app')"
              className="w-full pl-12 sm:pl-14 pr-5 py-4 sm:py-5 bg-white text-[#1d1d1f] rounded-full text-base sm:text-lg shadow-2xl focus:outline-none focus:ring-4 focus:ring-[#febd59]/50 transition-all placeholder:text-[#71717a]"
              data-testid="contact-help-search-input"
            />
          </div>

          <p className="mt-4 text-sm text-white/70">
            Prefer email?{' '}
            <a
              href="mailto:home@buildpilot.com.au"
              className="text-white underline underline-offset-2 hover:text-[#febd59] transition-colors"
              data-testid="contact-hero-email-link"
            >
              home@buildpilot.com.au
            </a>
          </p>

          {/* Trust stats */}
          <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg mx-auto">
            <TrustBadge icon={Home} stat="1,000+" label="Homes built" />
            <TrustBadge icon={Shield} stat="$500M+" label="Projects advised" />
            <TrustBadge icon={Smartphone} stat="4.6/5" label="App rating" />
          </div>

          {/* Social Media Links */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <span className="text-white/60 text-sm">Follow us:</span>
            <a
              href="https://instagram.com/buildpilot.com.au"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-all"
              aria-label="Instagram"
            >
              <Instagram className="w-5 h-5 text-white" />
            </a>
            <a
              href="https://www.facebook.com/buildpilot.com.au"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-all"
              aria-label="Facebook"
            >
              <Facebook className="w-5 h-5 text-white" />
            </a>
            <a
              href="https://www.linkedin.com/company/buildpilotau/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-all"
              aria-label="LinkedIn"
            >
              <Linkedin className="w-5 h-5 text-white" />
            </a>
          </div>
        </div>
      </section>

      {/* Topic / Category tiles */}
      <section className="py-12 sm:py-16 bg-[#fafbfc] border-b border-[#e5e7eb]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-6">
          <div className="mb-6 sm:mb-8">
            <h2 className="text-[22px] sm:text-3xl font-bold text-[#1d1d1f] tracking-[-0.02em] mb-2">How can we help?</h2>
            <p className="text-[#52525b] text-sm sm:text-base">Pick a topic to get matched or find answers fast.</p>
          </div>

          {filteredTopics.length === 0 ? (
            <div className="text-center py-12" data-testid="contact-help-no-results">
              <p className="text-[#52525b]">No topics match "{helpQuery}". Try a different search or send us a message below.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="contact-help-topics-grid">
              {filteredTopics.map((topic) => {
                const inner = (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 rounded-2xl shadow-sm border border-[#eef0f3] overflow-hidden flex items-center justify-center">
                        <img src={topic.img} alt={topic.title} loading="lazy" className="w-full h-full object-cover" />
                      </div>
                      {topic.badge && (
                        <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[#1d8efe] bg-[#1d8efe]/10 px-2.5 py-1 rounded-full">
                          {topic.badge}
                        </span>
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-[#1d1d1f] tracking-tight mb-2">{topic.title}</h3>
                    <p className="text-[#52525b] text-sm leading-relaxed mb-4">{topic.description}</p>
                    <span className="mt-auto inline-flex items-center gap-1.5 text-[#1d8efe] text-sm font-medium group-hover:gap-2.5 transition-all">
                      Explore
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </>
                );
                const cls = "bg-white p-6 rounded-2xl border border-[#e5e7eb] hover:border-[#1d8efe] hover:shadow-xl transition-all duration-300 flex flex-col group cursor-pointer";
                return topic.external ? (
                  <a key={topic.id} href={topic.link} target="_blank" rel="noopener noreferrer" className={cls} data-testid={topic.testId}>
                    {inner}
                  </a>
                ) : (
                  <Link key={topic.id} to={topic.link} className={cls} data-testid={topic.testId}>
                    {inner}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Main Form Section */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
            {/* Form - 3 columns */}
            <div className="lg:col-span-3">
              {isSubmitted ? (
                /* Success State */
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 sm:p-12 text-center">
                  <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-full bg-[var(--bp-color-surface-tinted)] flex items-center justify-center mx-auto mb-4 sm:mb-6">
                    <BrandIcon name="badge_check" className="w-8 sm:w-10 h-8 sm:h-10 text-green-600" />
                  </div>
                  <h2 className="text-[22px] sm:text-3xl font-semibold text-[#1d1d1f] mb-3 sm:mb-4">
                    Thanks for reaching out!
                  </h2>
                  <p className="text-[#86868b] mb-4 sm:mb-6 max-w-md mx-auto text-sm sm:text-base">
                    One of the BuildPilot team will be in touch as soon as we can. Keep an eye on your {formData.preferredContact === 'phone' ? 'phone' : 'inbox'}!
                  </p>
                  <div className="bg-white rounded-full p-3 sm:p-4 inline-flex items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                    <Clock className="w-4 sm:w-5 h-4 sm:h-5 text-[#1d8efe]" />
                    <span className="text-[#1d1d1f]">Average response time: <strong>2-4 hours</strong></span>
                  </div>
                </div>
              ) : (
                /* Multi-step Form - Compact on mobile */
                <div className="bg-[#f5f5f7] rounded-2xl p-4 sm:p-6 lg:p-8">
                  {/* Progress indicator - smaller on mobile */}
                  <div className="flex items-center gap-1.5 sm:gap-2 mb-5 sm:mb-8">
                    {[1, 2, 3].map((step) => (
                      <React.Fragment key={step}>
                        <div 
                          className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold transition-all ${
                            formStep >= step 
                              ? 'bg-[#1d8efe] text-white' 
                              : 'bg-gray-200 text-[#86868b]'
                          }`}
                        >
                          {formStep > step ? <BrandIcon name="badge_check" className="w-3.5 sm:w-4 h-3.5 sm:h-4" /> : step}
                        </div>
                        {step < 3 && (
                          <div className={`flex-1 h-0.5 sm:h-1 rounded-full transition-all ${
                            formStep > step ? 'bg-[#1d8efe]' : 'bg-gray-200'
                          }`} />
                        )}
                      </React.Fragment>
                    ))}
                  </div>

                  <form onSubmit={handleSubmit}>
                    {/* Step 1: Service Selection */}
                    {formStep === 1 && (
                      <div className="animate-fadeIn">
                        <h2 className="text-lg sm:text-2xl font-semibold text-[#1d1d1f] mb-1 sm:mb-2">
                          What type of project?
                        </h2>
                        <p className="text-[#86868b] mb-4 sm:mb-6 text-sm">Select one to continue</p>
                        
                        {/* 2x2 grid on mobile, more compact */}
                        <div className="grid grid-cols-2 gap-2 sm:gap-4 mb-5 sm:mb-8">
                          {services.map((service) => (
                            <ServiceCardCompact
                              key={service.id}
                              icon={service.icon}
                              title={service.title}
                              selected={formData.service === service.id}
                              onClick={() => handleServiceSelect(service.id)}
                            />
                          ))}
                        </div>

                        <button
                          type="button"
                          onClick={() => setFormStep(2)}
                          disabled={!canProceedStep1}
                          className={`w-full py-3 sm:py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all text-sm sm:text-base ${
                            canProceedStep1
                              ? 'bg-[#1d8efe] text-white hover:bg-[#3a6a9e]'
                              : 'bg-gray-200 text-[#86868b] cursor-not-allowed'
                          }`}
                        >
                          Continue
                          <ArrowRight className="w-4 sm:w-5 h-4 sm:h-5" />
                        </button>
                      </div>
                    )}

                    {/* Step 2: Contact Details */}
                    {formStep === 2 && (
                      <div className="animate-fadeIn">
                        <h2 className="text-lg sm:text-2xl font-semibold text-[#1d1d1f] mb-1 sm:mb-2">
                          Your contact details
                        </h2>
                        <p className="text-[#86868b] mb-4 sm:mb-6 text-sm">We'll use these to get in touch</p>

                        <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-[#1d1d1f] mb-1.5 sm:mb-2">
                              Your name *
                            </label>
                            <input
                              type="text"
                              name="name"
                              value={formData.name}
                              onChange={handleInputChange}
                              placeholder="John Smith"
                              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1d8efe] focus:border-transparent outline-none transition-all text-sm sm:text-base"
                              required
                            />
                          </div>

                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-[#1d1d1f] mb-1.5 sm:mb-2">
                              Email *
                            </label>
                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              placeholder="john@example.com"
                              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1d8efe] focus:border-transparent outline-none transition-all text-sm sm:text-base"
                              required
                            />
                          </div>

                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-[#1d1d1f] mb-1.5 sm:mb-2">
                              Phone *
                            </label>
                            <input
                              type="tel"
                              name="phone"
                              value={formData.phone}
                              onChange={handleInputChange}
                              placeholder="0412 345 678"
                              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1d8efe] focus:border-transparent outline-none transition-all text-sm sm:text-base"
                              required
                            />
                          </div>

                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-[#1d1d1f] mb-1.5 sm:mb-2">
                              Preferred contact
                            </label>
                            <div className="flex gap-4">
                              {['phone', 'email'].map((method) => (
                                <label key={method} className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="radio"
                                    name="preferredContact"
                                    value={method}
                                    checked={formData.preferredContact === method}
                                    onChange={handleInputChange}
                                    className="w-4 h-4 text-[#1d8efe] border-gray-300 focus:ring-[#1d8efe]"
                                  />
                                  <span className="text-[#1d1d1f] capitalize text-sm">{method}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div className="flex gap-2 sm:gap-3">
                          <button
                            type="button"
                            onClick={() => setFormStep(1)}
                            className="px-4 sm:px-6 py-3 sm:py-4 rounded-xl font-semibold border border-gray-300 text-[#1d1d1f] hover:bg-gray-100 transition-all text-sm sm:text-base"
                          >
                            Back
                          </button>
                          <button
                            type="button"
                            onClick={() => setFormStep(3)}
                            disabled={!canProceedStep2}
                            className={`flex-1 py-3 sm:py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all text-sm sm:text-base ${
                              canProceedStep2
                                ? 'bg-[#1d8efe] text-white hover:bg-[#3a6a9e]'
                                : 'bg-gray-200 text-[#86868b] cursor-not-allowed'
                            }`}
                          >
                            Continue
                            <ArrowRight className="w-4 sm:w-5 h-4 sm:h-5" />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Step 3: Project Details + reCAPTCHA - Compact on mobile */}
                    {formStep === 3 && (
                      <div className="animate-fadeIn">
                        <h2 className="text-lg sm:text-2xl font-semibold text-[#1d1d1f] mb-1 sm:mb-2">
                          About your project
                        </h2>
                        <p className="text-[#86868b] mb-4 sm:mb-6 text-sm">Optional but helps us prepare</p>

                        <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-[#1d1d1f] mb-1.5 sm:mb-2">
                              Where are you in the process?
                            </label>
                            <select
                              name="projectStage"
                              value={formData.projectStage}
                              onChange={handleInputChange}
                              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1d8efe] focus:border-transparent outline-none transition-all appearance-none text-sm sm:text-base"
                            >
                              <option value="">Select a stage...</option>
                              {projectStages.map((stage) => (
                                <option key={stage.value} value={stage.value}>
                                  {stage.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-[#1d1d1f] mb-1.5 sm:mb-2">
                              Anything else? (optional)
                            </label>
                            <textarea
                              name="message"
                              value={formData.message}
                              onChange={handleInputChange}
                              placeholder="Tell us about your project..."
                              rows={3}
                              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1d8efe] focus:border-transparent outline-none transition-all resize-none text-sm sm:text-base"
                            />
                          </div>

                          {/* Honeypot field. Invisible to humans + screen
                              readers; only naive bots fill it. Silently
                              rejected server-side. */}
                          <div
                            aria-hidden="true"
                            style={{
                              position: 'absolute',
                              left: '-10000px',
                              top: 'auto',
                              width: '1px',
                              height: '1px',
                              overflow: 'hidden',
                            }}
                          >
                            <label htmlFor="website">Website (leave blank)</label>
                            <input
                              type="text"
                              id="website"
                              name="website"
                              tabIndex="-1"
                              autoComplete="off"
                              value={honeypot}
                              onChange={(e) => setHoneypot(e.target.value)}
                            />
                          </div>

                          {/* reCAPTCHA - rendered only if site key is configured.
                              When disabled, honeypot + time-check still guard against bots. */}
                          {RECAPTCHA_ENABLED && (
                          <div className="pt-1 sm:pt-2 transform scale-[0.85] sm:scale-100 origin-left">
                            <ReCAPTCHA
                              ref={recaptchaRef}
                              sitekey={RECAPTCHA_SITE_KEY}
                              onChange={handleCaptchaChange}
                              onExpired={() => setCaptchaToken(null)}
                            />
                            {errors.captcha && (
                              <p className="text-red-500 text-xs sm:text-sm mt-2">{errors.captcha}</p>
                            )}
                          </div>
                          )}

                          <div className="pt-1">
                            <TermsAcceptCheckbox
                              checked={termsAccepted}
                              onChange={(v) => {
                                setTermsAccepted(v);
                                if (errors.terms) setErrors(prev => ({ ...prev, terms: null }));
                              }}
                              testId="contact-page-terms"
                            />
                            {errors.terms && (
                              <p className="text-red-500 text-xs sm:text-sm mt-2" data-testid="contact-page-terms-error">{errors.terms}</p>
                            )}
                          </div>
                        </div>

                        <div className="flex gap-2 sm:gap-3">
                          <button
                            type="button"
                            onClick={() => setFormStep(2)}
                            className="px-4 sm:px-6 py-3 sm:py-4 rounded-xl font-semibold border border-gray-300 text-[#1d1d1f] hover:bg-gray-100 transition-all text-sm sm:text-base"
                          >
                            Back
                          </button>
                          <button
                            type="submit"
                            disabled={isSubmitting}
                            className="flex-1 py-3 sm:py-4 rounded-xl font-semibold bg-[#e8713a] text-white hover:shadow-lg hover:shadow-orange-500/30 transition-all flex items-center justify-center gap-2 text-sm sm:text-base"
                          >
                            {isSubmitting ? (
                              <>
                                <Loader2 className="w-4 sm:w-5 h-4 sm:h-5 animate-spin" />
                                Sending...
                              </>
                            ) : (
                              <>
                                <Send className="w-4 sm:w-5 h-4 sm:h-5" />
                                Send Enquiry
                              </>
                            )}
                          </button>
                        </div>

                        <p className="text-[10px] sm:text-xs text-[#86868b] text-center mt-3 sm:mt-4">
                          By submitting, you agree to our{' '}
                          <Link to="/privacy" className="text-[#1d8efe] hover:underline">Privacy Policy</Link>
                        </p>
                      </div>
                    )}
                  </form>
                </div>
              )}
            </div>

            {/* Sidebar - 2 columns - Hidden on mobile, shown on lg */}
            <div className="hidden lg:block lg:col-span-2 space-y-6">
              {/* Why Contact Us - Animated Blue Gradient */}
              <div className="gradient-blue-bg rounded-2xl p-6 text-white">
                <h3 className="text-lg font-semibold mb-4">Why talk to BuildPilot?</h3>
                <ul className="space-y-3">
                  {[
                    "Get matched with listed local builders",
                    "Understand the true cost of your project",
                    "Avoid common building mistakes",
                    "Navigate building with confidence",
                    "Tailored support packages to suit your needs"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <BrandIcon name="badge_check" className="w-5 h-5 text-green-300 flex-shrink-0 mt-0.5" />
                      <span className="text-white/90">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Response time */}
              <div className="bg-[#f5f5f7] rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-[#1d8efe]/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-[#1d8efe]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#1d1d1f]">Fast Response</p>
                    <p className="text-sm text-[#86868b]">We reply within 2-4 hours</p>
                  </div>
                </div>
                <p className="text-sm text-[#86868b]">
                  Our team is available Monday to Friday, 8am-6pm ACST. Enquiries outside these hours will be answered the next business day.
                </p>
              </div>

              {/* Mobile contact options */}
              <div className="lg:hidden space-y-3">
                <a 
                  href="tel:1300885919" 
                  className="flex items-center gap-4 p-4 bg-[#1d1d1f] rounded-xl text-white"
                >
                  <Phone className="w-5 h-5" />
                  <span className="font-semibold">Call 1300 885 919</span>
                </a>
                <a 
                  href="mailto:home@buildpilot.com.au" 
                  className="flex items-center gap-4 p-4 bg-[#1d8efe] rounded-xl text-white"
                >
                  <Mail className="w-5 h-5" />
                  <span className="font-semibold">Email Us</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Carousel Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[#f5f5f7]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-[22px] sm:text-3xl font-semibold text-[#1d1d1f] mb-3">
              What Our Clients Say
            </h2>
            <p className="text-[#86868b]">
              Real stories from South Australian homeowners we've helped
            </p>
          </div>

          {/* Carousel */}
          <div className="relative">
            {/* Desktop: Show 3 cards */}
            <div className="hidden lg:grid lg:grid-cols-3 gap-6">
              {[0, 1, 2].map((offset) => {
                const index = (currentTestimonial + offset) % allTestimonials.length;
                return (
                  <TestimonialCard key={index} testimonial={allTestimonials[index]} />
                );
              })}
            </div>

            {/* Mobile/Tablet: Swipeable carousel */}
            <div 
              className="lg:hidden overflow-hidden touch-pan-x"
              onTouchStart={(e) => {
                const touch = e.touches[0];
                e.currentTarget.dataset.touchStartX = touch.clientX;
              }}
              onTouchEnd={(e) => {
                const touchStartX = parseFloat(e.currentTarget.dataset.touchStartX);
                const touchEndX = e.changedTouches[0].clientX;
                const diff = touchStartX - touchEndX;
                
                if (Math.abs(diff) > 50) {
                  if (diff > 0) {
                    // Swipe left - next
                    setCurrentTestimonial((prev) => (prev + 1) % allTestimonials.length);
                  } else {
                    // Swipe right - previous
                    setCurrentTestimonial((prev) => (prev - 1 + allTestimonials.length) % allTestimonials.length);
                  }
                }
              }}
            >
              <div 
                className="flex transition-transform duration-300 ease-out"
                style={{ transform: `translateX(-${currentTestimonial * 100}%)` }}
              >
                {allTestimonials.map((testimonial, index) => (
                  <div key={index} className="w-full flex-shrink-0 px-1">
                    <TestimonialCard testimonial={testimonial} />
                  </div>
                ))}
              </div>
              <p className="text-center text-xs text-[#86868b] mt-4">
                ← Swipe to see more →
              </p>
            </div>

            {/* Navigation arrows - Desktop only */}
            <div className="hidden lg:flex justify-center items-center gap-4 mt-8">
              <button
                onClick={prevTestimonial}
                className="w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-[#1d8efe] hover:text-white hover:border-[#1d8efe] transition-all"
                aria-label="Previous testimonial"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              
              {/* Dots indicator */}
              <div className="flex gap-2">
                {allTestimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentTestimonial(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentTestimonial ? 'bg-[#1d8efe] w-6' : 'bg-gray-300'
                    }`}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>

              <button
                onClick={nextTestimonial}
                className="w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-[#1d8efe] hover:text-white hover:border-[#1d8efe] transition-all"
                aria-label="Next testimonial"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Dots indicator - Mobile */}
            <div className="flex lg:hidden justify-center gap-1.5 mt-4">
              {allTestimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentTestimonial ? 'bg-[#1d8efe] w-4' : 'bg-gray-300'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>

            {/* Counter */}
            <p className="text-center text-sm text-[#86868b] mt-4">
              {currentTestimonial + 1} of {allTestimonials.length} reviews
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section - SEO Powerhouse */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-[800px] mx-auto px-5 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-[22px] sm:text-3xl font-semibold text-[#1d1d1f] mb-3">
              Frequently Asked Questions
            </h2>
            <p className="text-[#86868b]">
              Everything you need to know about working with BuildPilot
            </p>
          </div>

          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm">
            {faqs.map((faq, index) => (
              <FaqItem
                key={index}
                question={faq.q}
                answer={faq.a}
                isOpen={openFaq === index}
                onClick={() => setOpenFaq(openFaq === index ? -1 : index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas - Updated */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[#f5f5f7]">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-[22px] sm:text-3xl font-semibold text-[#1d1d1f] mb-3">
              Our Service Coverage
            </h2>
            <p className="text-[#86868b] max-w-2xl mx-auto">
              One-on-one building support is currently available for <strong>South Australian residents</strong>. 
              Our self-guided BuildPilot app is available <strong>nationwide</strong>.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* One-on-One Support */}
            <div className="bg-[#1d8efe] rounded-2xl p-8 text-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                  <BrandIcon name="users" className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold">One-on-One Support</h3>
                  <p className="text-white/70 text-sm">Personalised guidance</p>
                </div>
              </div>
              <p className="text-white/80 mb-6">
                Work directly with the BuildPilot team for personalised support throughout your building journey.
              </p>
              <div className="flex items-center gap-2 bg-white/10 rounded-xl p-4">
                <BrandIcon name="map_pin" className="w-5 h-5 text-white/60" />
                <span className="font-medium">South Australia Only</span>
              </div>
              <ul className="mt-4 space-y-2 text-sm text-white/70">
                <li>• Greater Adelaide & suburbs</li>
                <li>• Adelaide Hills</li>
                <li>• Regional SA (case by case)</li>
              </ul>
            </div>

            {/* BuildPilot App - Light purple like homepage */}
            <div className="announcement-gradient rounded-2xl p-8 border border-[#e5e7eb]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-[#f5f5f7] border border-[#e5e7eb] flex items-center justify-center">
                  <Smartphone className="w-6 h-6 text-[#1d8efe]" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-[#1d1d1f]">BuildPilot App</h3>
                  <p className="text-[#1d8efe] text-sm font-medium">Self-guided option</p>
                </div>
              </div>
              <p className="text-[#86868b] mb-6">
                Access our comprehensive building tools, guides, and resources at your own pace.
              </p>
              <div className="flex items-center gap-2 bg-[var(--bp-color-surface-tinted)] rounded-xl p-4 text-[var(--bp-color-ink)]">
                <BrandIcon name="badge_check" className="w-5 h-5" />
                <span className="font-medium">Available Nationwide</span>
              </div>
              <div className="mt-4 flex items-center gap-3">
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <BrandIcon name="star" key={i} className="w-4 h-4 fill-[#1d8efe] text-[#1d8efe]" />
                  ))}
                </div>
                <span className="text-sm text-[#86868b]">4.6/5 App Store rating</span>
              </div>
              <a
                href="https://buildpilot.app"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-[#1d8efe] font-medium hover:underline"
              >
                Download the app
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA - Dark gradient like hero */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[#1d1d1f]">
        <div className="max-w-[800px] mx-auto px-5 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-semibold text-white mb-4">
            Ready to start your building journey?
          </h2>
          <p className="text-white/70 mb-8 max-w-lg mx-auto">
            Join over 1,000 South Australian homeowners who've trusted BuildPilot to guide them through their building project.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:1300885919"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#1d1d1f] font-semibold rounded-full hover:bg-gray-100 transition-colors"
            >
              <Phone className="w-5 h-5" />
              Call 1300 885 919
            </a>
            <button
              onClick={() => window.scrollTo({ top: 400, behavior: 'smooth' })}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#e8713a] text-white font-semibold rounded-full hover:shadow-lg hover:shadow-orange-500/30 transition-all"
            >
              Fill Out Form
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>

          {/* Social links in CTA */}
          <div className="flex justify-center items-center gap-4 mt-10 pt-8 border-t border-white/10">
            <span className="text-white/60 text-sm">Connect with us:</span>
            <a href="https://instagram.com/buildpilot.com.au" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-all">
              <Instagram className="w-5 h-5 text-white" />
            </a>
            <a href="https://www.facebook.com/buildpilot.com.au" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-all">
              <Facebook className="w-5 h-5 text-white" />
            </a>
            <a href="https://www.linkedin.com/company/buildpilotau/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-all">
              <Linkedin className="w-5 h-5 text-white" />
            </a>
          </div>
        </div>
      </section>

      {/* Related Guidance - Internal Links for SEO */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[#f5f5f7] border-t border-gray-200">
        <div className="max-w-[1080px] mx-auto px-5 sm:px-6 lg:px-8">
          <div className="text-center mb-6 sm:mb-8">
            <h3 className="text-lg sm:text-xl font-semibold text-[#1d1d1f]">Explore Our Services</h3>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {relatedGuidance.map((guide, index) => (
              <Link 
                key={index}
                to={guide.slug} 
                className="bg-white p-4 sm:p-5 rounded-xl hover:shadow-md transition-all group"
              >
                <h4 className="text-sm sm:text-base font-medium text-[#1d1d1f] group-hover:text-[#1d8efe] transition-colors mb-1">{guide.title}</h4>
                <p className="text-xs text-[#86868b] line-clamp-2 hidden sm:block">{guide.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      {/* CSS for animations and gradients */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        
        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        .hero-gradient-blue {
          background: linear-gradient(-45deg, #1d8efe, #3a6a9e, #2d5a8a, #1d8efe);
          background-size: 400% 400%;
          animation: gradientShift 8s ease infinite;
        }
        
        @keyframes orangeGradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        .gradient-text-orange-animated {
          background: linear-gradient(-45deg, #e8713a, #de4f2c, #f59e0b, #e8713a);
          background-size: 400% 400%;
          animation: orangeGradientShift 4s ease infinite;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      `}</style>
      
      {/* SEO Schema */}
      <PageSEOFooter 
        breadcrumbs={[
          { name: 'Contact', url: '/contact' }
        ]}
        includeLocalBusiness={true}
        includeReviews={false}
      />
    </div>
  );
};

export default ContactPage;
